/**
 * Team Invitation Screen - Handles deep link team invitations
 * URL: /motivation/challenge/:challengeId/teams/:teamId/join?inviteCode=xxx
 *
 * Shows team info and allows the user to accept or decline the invitation.
 * If already in a team for this challenge, shows appropriate message.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useCallback, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Alert, Spinner } from 'reactstrap';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { useRouteChallenge, useMyRouteChallengeTeam } from '../../Hooks/useChallengeQueries';
import { useJoinRouteChallengeTeam } from '../../Hooks/useRoutechallengeMutations';
import { RouteChallengeTeamApi } from '../../Model/Engagement/RouteChallenge/RouteChallengeTeam';
import { getMediaMediumOrSmallest } from '../../Model/Media/Media';
import { Button } from '../../Components/Button';
import { Theme } from '../../Theme/Theme';
import { useQuery } from '@tanstack/react-query';

const PageContainer = styled(Container)`
  padding-top: 48px;
  padding-bottom: 48px;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 480px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  text-align: center;
`;

const TeamImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 16px;
  border: 3px solid ${Theme.Colors.primary};
`;

const TeamImagePlaceholder = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${Theme.Colors.lightestGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 32px;
  border: 3px solid ${Theme.Colors.primary};
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: ${Theme.Colors.warmGrey};
  margin-bottom: 24px;
  line-height: 1.5;
`;

const TeamName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin-bottom: 4px;
`;

const TeamMeta = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.warmGrey};
  margin-bottom: 24px;
`;

const ChallengeName = styled.p`
  font-size: 13px;
  color: ${Theme.Colors.primary};
  font-weight: 600;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 16px 0;
`;

const SuccessEmoji = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px;
`;

export const TeamInvitationScreen: React.FC = observer(() => {
  const { challengeId, teamId } = useParams<{ challengeId: string; teamId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTrans();

  const inviteCode = searchParams.get('inviteCode') ?? '';

  const [joinState, setJoinState] = useState<'idle' | 'joining' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch challenge info
  const { data: challenge, isLoading: isLoadingChallenge } = useRouteChallenge(challengeId);
  const challengeName = useLocalized(challenge?.name as any);

  // Fetch team info
  const { data: team, isLoading: isLoadingTeam } = useQuery({
    queryKey: ['route-challenge-team', teamId],
    queryFn: () => RouteChallengeTeamApi.findOne(teamId!),
    enabled: !!teamId,
    staleTime: 60 * 1000,
  });

  // Check if user already has a team in this challenge
  const { data: myTeam, isLoading: isLoadingMyTeam } = useMyRouteChallengeTeam(challengeId);

  const joinMutation = useJoinRouteChallengeTeam();

  const teamImageUrl = team?.image ? getMediaMediumOrSmallest(team.image) : undefined;
  const isLoading = isLoadingChallenge || isLoadingTeam || isLoadingMyTeam;
  const alreadyInThisTeam = myTeam?.id === teamId;
  const alreadyInDifferentTeam = !!myTeam && myTeam.id !== teamId;
  const challengeEnded = challenge?.ended ?? false;
  const teamFull = team ? !team.hasSeatsAvailable : false;

  const handleJoin = useCallback(async () => {
    if (!teamId || !inviteCode) return;

    setJoinState('joining');
    setErrorMessage('');

    try {
      await joinMutation.mutateAsync({ teamId, inviteCode });
      setJoinState('success');
    } catch (error: any) {
      setJoinState('error');
      const msg = error?.response?.data?.message || error?.message || '';
      if (msg.includes('already')) {
        setErrorMessage(t('teamInvitation.error.alreadyMember', { defaultValue: 'You are already a member of a team in this challenge.' }));
      } else if (msg.includes('full') || msg.includes('seats')) {
        setErrorMessage(t('teamInvitation.error.teamFull', { defaultValue: 'This team is full.' }));
      } else if (msg.includes('code') || msg.includes('invalid')) {
        setErrorMessage(t('teamInvitation.error.invalidCode', { defaultValue: 'The invitation link is invalid or has expired.' }));
      } else {
        setErrorMessage(t('teamInvitation.error.generic', { defaultValue: 'Something went wrong. Please try again.' }));
      }
    }
  }, [teamId, inviteCode, joinMutation, t]);

  const handleGoToChallenge = useCallback(() => {
    navigate(`/motivation/challenge/${challengeId}`);
  }, [navigate, challengeId]);

  const handleDecline = useCallback(() => {
    navigate(`/motivation/challenge/${challengeId}`);
  }, [navigate, challengeId]);

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <Card>
          <LoadingContainer>
            <Spinner color="primary" />
            <Subtitle>{t('teamInvitation.loading', { defaultValue: 'Loading invitation...' })}</Subtitle>
          </LoadingContainer>
        </Card>
      </PageContainer>
    );
  }

  // No invite code
  if (!inviteCode) {
    return (
      <PageContainer>
        <Card>
          <SuccessEmoji>🔗</SuccessEmoji>
          <Title>{t('teamInvitation.invalidLink', { defaultValue: 'Invalid Invitation Link' })}</Title>
          <Subtitle>{t('teamInvitation.invalidLinkMessage', { defaultValue: 'This invitation link is missing required information.' })}</Subtitle>
          <Button onClick={handleGoToChallenge}>
            {t('teamInvitation.viewChallenge', { defaultValue: 'View Challenge' })}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Challenge not found
  if (!challenge) {
    return (
      <PageContainer>
        <Card>
          <SuccessEmoji>🔍</SuccessEmoji>
          <Title>{t('teamInvitation.challengeNotFound', { defaultValue: 'Challenge Not Found' })}</Title>
          <Subtitle>{t('teamInvitation.challengeNotFoundMessage', { defaultValue: 'This challenge may no longer be available.' })}</Subtitle>
          <Button onClick={() => navigate('/motivation')}>
            {t('teamInvitation.backToMotivation', { defaultValue: 'Back to Motivation' })}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Team not found
  if (!team) {
    return (
      <PageContainer>
        <Card>
          <ChallengeName>{challengeName}</ChallengeName>
          <SuccessEmoji>🔍</SuccessEmoji>
          <Title>{t('teamInvitation.teamNotFound', { defaultValue: 'Team Not Found' })}</Title>
          <Subtitle>{t('teamInvitation.teamNotFoundMessage', { defaultValue: 'This team may no longer exist.' })}</Subtitle>
          <Button onClick={handleGoToChallenge}>
            {t('teamInvitation.viewChallenge', { defaultValue: 'View Challenge' })}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Successfully joined
  if (joinState === 'success') {
    return (
      <PageContainer>
        <Card>
          <SuccessContainer>
            <SuccessEmoji>🎉</SuccessEmoji>
            <Title>{t('teamInvitation.success', { defaultValue: 'Welcome to the Team!' })}</Title>
            <Subtitle>
              {t('teamInvitation.successMessage', {
                defaultValue: 'You have successfully joined {{teamName}}.',
                teamName: team.name,
              })}
            </Subtitle>
            <Button onClick={handleGoToChallenge}>
              {t('teamInvitation.goToChallenge', { defaultValue: 'Go to Challenge' })}
            </Button>
          </SuccessContainer>
        </Card>
      </PageContainer>
    );
  }

  // Already in this team
  if (alreadyInThisTeam) {
    return (
      <PageContainer>
        <Card>
          <ChallengeName>{challengeName}</ChallengeName>
          {teamImageUrl ? <TeamImage src={teamImageUrl} alt={team.name} /> : <TeamImagePlaceholder>👥</TeamImagePlaceholder>}
          <TeamName>{team.name}</TeamName>
          <SuccessEmoji style={{ fontSize: 32, marginBottom: 8 }}>✅</SuccessEmoji>
          <Subtitle>{t('teamInvitation.alreadyInTeam', { defaultValue: 'You are already a member of this team.' })}</Subtitle>
          <Button onClick={handleGoToChallenge}>
            {t('teamInvitation.viewChallenge', { defaultValue: 'View Challenge' })}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Already in a different team
  if (alreadyInDifferentTeam) {
    return (
      <PageContainer>
        <Card>
          <ChallengeName>{challengeName}</ChallengeName>
          {teamImageUrl ? <TeamImage src={teamImageUrl} alt={team.name} /> : <TeamImagePlaceholder>👥</TeamImagePlaceholder>}
          <TeamName>{team.name}</TeamName>
          <Subtitle>
            {t('teamInvitation.alreadyInDifferentTeam', {
              defaultValue: 'You are already in a different team for this challenge. Leave your current team first to join this one.',
            })}
          </Subtitle>
          <Button onClick={handleGoToChallenge}>
            {t('teamInvitation.viewChallenge', { defaultValue: 'View Challenge' })}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Challenge ended
  if (challengeEnded) {
    return (
      <PageContainer>
        <Card>
          <ChallengeName>{challengeName}</ChallengeName>
          <SuccessEmoji>⏰</SuccessEmoji>
          <Title>{t('teamInvitation.challengeEnded', { defaultValue: 'Challenge Ended' })}</Title>
          <Subtitle>{t('teamInvitation.challengeEndedMessage', { defaultValue: 'This challenge has already ended.' })}</Subtitle>
          <Button onClick={handleGoToChallenge}>
            {t('teamInvitation.viewChallenge', { defaultValue: 'View Challenge' })}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Team full
  if (teamFull) {
    return (
      <PageContainer>
        <Card>
          <ChallengeName>{challengeName}</ChallengeName>
          {teamImageUrl ? <TeamImage src={teamImageUrl} alt={team.name} /> : <TeamImagePlaceholder>👥</TeamImagePlaceholder>}
          <TeamName>{team.name}</TeamName>
          <Subtitle>{t('teamInvitation.teamFull', { defaultValue: 'This team is already full.' })}</Subtitle>
          <Button onClick={handleGoToChallenge}>
            {t('teamInvitation.viewChallenge', { defaultValue: 'View Challenge' })}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Main invitation view
  return (
    <PageContainer>
      <Card>
        <ChallengeName>{challengeName}</ChallengeName>
        {teamImageUrl ? <TeamImage src={teamImageUrl} alt={team.name} /> : <TeamImagePlaceholder>👥</TeamImagePlaceholder>}
        <TeamName>{team.name}</TeamName>
        <TeamMeta>
          {t('teamInvitation.memberCount', {
            defaultValue: '{{count}} / {{max}} members',
            count: team.memberCount,
            max: team.maxAllowedMembers,
          })}
        </TeamMeta>

        <Title>{t('teamInvitation.title', { defaultValue: 'You\'ve been invited!' })}</Title>
        <Subtitle>
          {t('teamInvitation.description', {
            defaultValue: 'Join {{teamName}} and compete together in this challenge.',
            teamName: team.name,
          })}
        </Subtitle>

        {joinState === 'error' && (
          <Alert color="danger" style={{ marginBottom: 16, textAlign: 'left' }}>
            {errorMessage}
          </Alert>
        )}

        <ButtonGroup>
          <Button onClick={handleJoin} disabled={joinState === 'joining'} color="success">
            {joinState === 'joining'
              ? t('teamInvitation.joining', { defaultValue: 'Joining...' })
              : t('teamInvitation.accept', { defaultValue: 'Accept Invitation' })}
          </Button>
          <Button onClick={handleDecline} color="secondary" disabled={joinState === 'joining'}>
            {t('teamInvitation.decline', { defaultValue: 'Decline' })}
          </Button>
        </ButtonGroup>
      </Card>
    </PageContainer>
  );
});
